import React from 'react'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import useLlmLanes, { type LlmLanesScope } from '../../hooks/useLlmLanes'
import {
  censusIsIdle,
  formatLaneLimits,
  formatWaitAge,
  laneDepth,
  laneTypeShares,
} from './llmLanesModel'
import { formatTimestamp } from './laneQueueModel'
import type { LlmLaneDepth } from '../../types/llmLanes'

/**
 * LlmLanesPanel — the model lanes, as the queue itself sees them.
 *
 * Every other lane surface in the product counts autocoder_coding_batch rows.
 * This one reads `llm_request`, the thing the drain actually pops, which is why
 * the Traffic column can show Theme AI competing for the same lane as a coding
 * run. That is the whole reason this panel exists.
 *
 * One component, two callers: the customer-facing Activity tab passes a job
 * scope, the admin throughput page passes the unfiltered admin scope. The
 * fetching lives here (props are the *variant*) so neither caller has to know
 * about polling.
 *
 * Every lane this environment is CONFIGURED to call is listed even when it is
 * idle — the server guarantees it — so the reader's list never reshuffles as
 * work arrives and drains. Lanes with no credentials here are not listed at
 * all: they are not idle queues, they are models this deployment cannot call,
 * and showing the whole catalog read as a dozen queues a customer's work might
 * be sitting in.
 */

export interface LlmLanesPanelProps {
  /** Which census to read. See `LlmLanesScope`. */
  scope: LlmLanesScope
  /** False while the host view is hidden — a hidden panel must not poll. */
  active?: boolean
  /** Optional heading; omitted renders the table with no title bar. */
  title?: string
  subtitle?: string
}

const HEAD_CELLS = ['Lane', 'Waiting', 'In flight', 'Oldest wait', 'Traffic', 'Limits']

const LaneRow: React.FC<{ lane: LlmLaneDepth; now: number }> = ({ lane, now }) => {
  const theme = useTheme()
  const busy = laneDepth(lane) > 0
  const shares = laneTypeShares(lane)
  const limits = formatLaneLimits(lane.limits)
  const wait = formatWaitAge(lane.oldest_pending_at, now)

  return (
    <TableRow
      // Keyed by the lane id, which IS the model id (llm.ModelID), never by the
      // display name or the row's position: every catalog lane is always listed,
      // but a reader sorts nothing here and the display names are catalog copy.
      data-testid={`llm-lane-row-${lane.lane}`}
      sx={{
        backgroundColor: busy ? alpha(theme.palette.info.main, 0.06) : 'transparent',
        '& td': { color: busy ? 'text.primary' : 'text.secondary' },
      }}
    >
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: busy ? 700 : 500 }}>
          {lane.display_name || lane.lane}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {lane.lane}
        </Typography>
      </TableCell>

      <TableCell align="right">
        <Typography variant="body2" sx={{ fontWeight: lane.pending ? 700 : 400 }}>
          {lane.pending.toLocaleString()}
        </Typography>
      </TableCell>

      <TableCell align="right">
        <Typography
          variant="body2"
          sx={{ fontWeight: lane.running ? 700 : 400, color: lane.running ? 'info.main' : undefined }}
        >
          {lane.running.toLocaleString()}
        </Typography>
      </TableCell>

      {/* An idle lane has no oldest-pending instant. "0s" would be a lie, so
          the cell says there is nothing waiting instead. */}
      <TableCell align="right">
        {wait ? (
          <Tooltip title={`Queued ${formatTimestamp(lane.oldest_pending_at) ?? 'at an unknown time'}`}>
            <Typography variant="body2">{wait}</Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.disabled">—</Typography>
        )}
      </TableCell>

      <TableCell>
        {shares.length ? (
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {shares.map(share => (
              <Chip
                key={share.type}
                size="small"
                variant="outlined"
                label={`${share.label} · ${share.count.toLocaleString()}`}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.disabled">—</Typography>
        )}
      </TableCell>

      {/* All-zero limits mean no limiter is seeded for this lane in the API
          process — NOT "unlimited", and not a cap of zero. Say so in words. */}
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {limits ?? 'no limiter here'}
        </Typography>
        {limits && lane.limits.concurrency_source ? (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
            {lane.limits.concurrency_source}
          </Typography>
        ) : null}
      </TableCell>
    </TableRow>
  )
}

const LlmLanesPanel: React.FC<LlmLanesPanelProps> = ({
  scope,
  active = true,
  title,
  subtitle,
}) => {
  const { data, loading, isFetching, error, refetch } = useLlmLanes(scope, active)

  // One instant for the whole render, so every wait age in the table is
  // measured from the same clock reading.
  const now = Date.now()
  const lanes = data?.lanes ?? []

  return (
    <Paper elevation={1} sx={{ p: 0, overflow: 'hidden' }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title ?? 'Model lanes'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subtitle ??
              'Live depth of the LLM request queue — every client on the lane, not just coding.'}
            {data ? ` · as of ${formatTimestamp(data.as_of) ?? '—'}` : ''}
          </Typography>
        </Box>
        <Tooltip title="Refresh now">
          <span>
            <IconButton
              color="info"
              onClick={refetch}
              disabled={isFetching}
              aria-label="Refresh model lanes"
            >
              {isFetching ? <CircularProgress size={20} color="info" /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          Lane census unavailable — {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ p: 2 }}>
          {[0, 1, 2, 3].map(i => (
            <Skeleton key={i} height={44} />
          ))}
        </Box>
      ) : null}

      {data && (
        <>
          {censusIsIdle(lanes) && (
            <Alert severity="info" sx={{ m: 2 }} variant="outlined">
              Nothing queued right now — every lane is idle.
            </Alert>
          )}
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {HEAD_CELLS.map(head => (
                    <TableCell
                      key={head}
                      align={head === 'Waiting' || head === 'In flight' || head === 'Oldest wait'
                        ? 'right'
                        : 'left'}
                      sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {lanes.map(lane => (
                  <LaneRow key={lane.lane} lane={lane} now={now} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  )
}

export default LlmLanesPanel
